import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    useGetAdminMarathonQuery,
    useGetAdminMarathonParticipantsQuery,
    useGetAdminMarathonTicketsQuery,
    useGetAdminMarathonBetsQuery,
    useGetAdminMarathonStatisticsQuery,
    useGetAdminMarathonWinnersQuery,
    usePayMarathonParticipantMutation,
    useTransferMarathonParticipantToCompanyMutation,
} from "../api/marathonAdmin";
import { useRestoreMarathonUserAndTicketsMutation } from "../api";
import { ReplaceParticipantModal } from "../components/marathon/ReplaceParticipantModal";
import { MarathonUserAccountsModal } from "../components/marathon/MarathonUserAccountsModal";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";
import { ReplaceIcon, ArchiveRestoreIcon, ClipboardCheckIcon, BanknoteArrowUpIcon, ListIcon } from 'lucide-react';

type TabKey = "overview" | "participants" | "tickets" | "bets" | "winners";

const tabItems: { key: TabKey; labelKey: string }[] = [
    { key: "overview", labelKey: "marathon:tabs.overview" },
    { key: "participants", labelKey: "marathon:tabs.participants" },
    { key: "tickets", labelKey: "marathon:tabs.tickets" },
    { key: "bets", labelKey: "marathon:tabs.bets" },
    { key: "winners", labelKey: "marathon:tabs.winners" },
];

const participantStatusOptions = [
    { value: "all", label: "marathon:statuses.all" },
    { value: "0", label: "marathon:participant_status.active" },
    { value: "1", label: "marathon:participant_status.cancelled" },
];

const ticketStatusOptions = [
    { value: "all", label: "marathon:statuses.all" },
    { value: "0", label: "marathon:ticket_status.active" },
    { value: "1", label: "marathon:ticket_status.winner" },
    { value: "2", label: "marathon:ticket_status.deleted" },
    {
        value: "3",
        label: "marathon:ticket_status.owner_won_with_other_ticket",
    },
];

const MarathonDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const marathonId = Number(id);
    const navigate = useNavigate();
    const { t } = useTranslation(["marathon", "common"]);

    const [activeTab, setActiveTab] = useState<TabKey>("overview");
    const [participantFilters, setParticipantFilters] = useState({
        status: "all",
        paid: "all",
        search: "",
        page: 1,
    });
    const [ticketFilters, setTicketFilters] = useState({
        status: "all",
        search: "",
        page: 1,
    });
    const [betFilters, setBetFilters] = useState({
        search: "",
        page: 1,
    });
    const [replaceModalOpen, setReplaceModalOpen] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState<number | null>(null);
    const [transferToCompanyOpen, setTransferToCompanyOpen] = useState(false);
    const [transferParticipantId, setTransferParticipantId] = useState<number | null>(null);
    const [transferParticipantLabel, setTransferParticipantLabel] = useState<string>("");
    const [accountsModalOpen, setAccountsModalOpen] = useState(false);
    const [selectedAccountsParticipant, setSelectedAccountsParticipant] = useState<{ id: number, userId: number, name: string } | null>(null);

    const [payParticipant, { isLoading: isPayingParticipant }] =
        usePayMarathonParticipantMutation();
    const [transferToCompany, { isLoading: isTransferringToCompany }] =
        useTransferMarathonParticipantToCompanyMutation();
    const [restoreParticipant, { isLoading: isRestoringParticipant }] =
        useRestoreMarathonUserAndTicketsMutation();

    const { showError, showSuccess, notification, hideNotification } =
        useNotification();

    const handlePayParticipant = async (participantId: number) => {
        if (!window.confirm(t("marathon:confirm_pay_participant"))) {
            return;
        }
        try {
            await payParticipant({
                marathonId,
                participantId,
            }).unwrap();
            // Данные обновятся автоматически через invalidatesTags
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    t("marathon:pay_participant_error")
            );
        }
    };

    const handleRestoreParticipant = async (userId: number) => {
        if (!window.confirm("Вы уверены, что хотите восстановить пользователя?")) {
            return;
        }

        try {
            const res = await restoreParticipant({ 
                user_id: userId, 
                marathon_id: marathonId 
            }).unwrap();
            showSuccess(res.message || "Пользователь успешно восстановлен");
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Не удалось восстановить пользователя"
            );
        }
    };

    const handleOpenTransferToCompany = (participantId: number, label: string) => {
        setTransferParticipantId(participantId);
        setTransferParticipantLabel(label);
        setTransferToCompanyOpen(true);
    };

    const handleConfirmTransferToCompany = async () => {
        if (!transferParticipantId) {
            return;
        }

        try {
            const res = await transferToCompany({
                marathonId,
                participantId: transferParticipantId,
            }).unwrap();
            showSuccess(res.message || "Переоформление выполнено");
            setTransferToCompanyOpen(false);
            setTransferParticipantId(null);
            setTransferParticipantLabel("");
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Не удалось переоформить участника на компанию"
            );
        }
    };

    const {
        data: marathon,
        isLoading: marathonLoading,
        isFetching: marathonFetching,
    } = useGetAdminMarathonQuery(marathonId, {
        skip: !marathonId,
    });

    const { data: statsData, isFetching: statsFetching } =
        useGetAdminMarathonStatisticsQuery(marathonId, {
            skip: !marathonId,
        });

    const participantsQueryEnabled =
        activeTab === "participants" && Boolean(marathonId);
    const ticketsQueryEnabled =
        activeTab === "tickets" && Boolean(marathonId);
    const betsQueryEnabled = activeTab === "bets" && Boolean(marathonId);
    const winnersQueryEnabled =
        activeTab === "winners" && Boolean(marathonId);

    const {
        data: participantsData,
        isFetching: participantsFetching,
    } = useGetAdminMarathonParticipantsQuery(
        {
            marathonId,
            page: participantFilters.page,
            per_page: 250,
            status:
                participantFilters.status !== "all"
                    ? Number(participantFilters.status)
                    : undefined,
            paid:
                participantFilters.paid === "all"
                    ? undefined
                    : participantFilters.paid === "paid",
            search: participantFilters.search || undefined,
        },
        {
            skip: !participantsQueryEnabled,
        }
    );

    const { data: ticketsData, isFetching: ticketsFetching } =
        useGetAdminMarathonTicketsQuery(
            {
                marathonId,
                page: ticketFilters.page,
                per_page: 250,
                status:
                    ticketFilters.status !== "all"
                        ? Number(ticketFilters.status)
                        : undefined,
                search: ticketFilters.search || undefined,
            },
            {
                skip: !ticketsQueryEnabled,
            }
        );

    const { data: betsData, isFetching: betsFetching } =
        useGetAdminMarathonBetsQuery(
            {
                marathonId,
                page: betFilters.page,
                per_page: 250,
                search: betFilters.search || undefined,
            },
            {
                skip: !betsQueryEnabled,
            }
        );

    const { data: winnersData, isFetching: winnersFetching } =
        useGetAdminMarathonWinnersQuery(marathonId, {
            skip: !winnersQueryEnabled,
        });

    const summaryMetrics = useMemo(() => {
        const summary = statsData?.summary;
        const financials = marathon?.financial_summary;

        return [
            {
                label: t("marathon:metrics.participants"),
                value: summary?.participants_total ?? 0,
            },
            {
                label: t("marathon:metrics.paid"),
                value: summary?.participants_paid ?? 0,
            },
            {
                label: t("marathon:metrics.pending"),
                value: summary?.participants_pending ?? 0,
            },
            {
                label: t("marathon:metrics.tickets"),
                value: summary?.tickets_total ?? 0,
            },
            {
                label: t("marathon:metrics.bets"),
                value: summary?.bets_total ?? 0,
            },
            {
                label: t("marathon:metrics.collected"),
                value: financials?.paid_amount ?? 0,
                formatted: true,
            },
        ];
    }, [statsData, marathon, t]);

    const overviewPayments = statsData?.latest_payments ?? [];
    const overviewTopParticipants = statsData?.top_participants ?? [];

    const handleParticipantFilterChange = (
        key: keyof typeof participantFilters,
        value: string
    ) => {
        setParticipantFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    };

    const handleTicketFilterChange = (
        key: keyof typeof ticketFilters,
        value: string
    ) => {
        setTicketFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    };

    const handleBetFilterChange = (value: string) => {
        setBetFilters((prev) => ({ ...prev, search: value, page: 1 }));
    };

    const renderTabButton = (tab: TabKey, label: string) => (
        <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
                activeTab === tab
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
        >
            {label}
        </button>
    );

    const renderStatusBadge = (label: string) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            {label}
        </span>
    );

    if (!marathonId || Number.isNaN(marathonId)) {
        return (
            <div className="p-6">
                <p className="text-red-600">{t("marathon:no_data")}</p>
                <Link
                    to="/dashboard/marathons"
                    className="text-blue-600 hover:text-blue-800"
                >
                    {t("marathon:back_to_list")}
                </Link>
            </div>
        );
    }

    return (
        <div className="px-4 py-6 space-y-6">
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                            {t("marathon:back_to_list")}
                        </button>
                    </p>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {t("marathon:details_title", { id: marathonId })}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {marathon?.type_label} • {marathon?.status_label}
                    </p>
                </div>
            </div>

            {(marathonLoading || marathonFetching) && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t("common:loading")}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {tabItems.map((tab) =>
                    renderTabButton(tab.key, t(tab.labelKey))
                )}
            </div>

            {activeTab === "overview" && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {summaryMetrics.map((metric) => (
                            <div
                                key={metric.label}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                            >
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {metric.label}
                                </p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {metric.formatted
                                        ? metric.value.toLocaleString()
                                        : metric.value}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {t("marathon:recent_payments")}
                            </h3>
                            {statsFetching ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("common:loading")}
                                </p>
                            ) : overviewPayments.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("marathon:no_data")}
                                </p>
                            ) : (
                                <ul className="space-y-3">
                                    {overviewPayments.map((payment, idx) => (
                                        <li
                                            key={`${payment.user?.id}-${idx}`}
                                            className="flex items-center justify-between"
                                        >
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {payment.user?.name ?? "—"}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {payment.paid_at
                                                        ? new Date(
                                                              payment.paid_at
                                                          ).toLocaleString()
                                                        : "—"}
                                                </p>
                                            </div>
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {payment.amount}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {t("marathon:top_participants")}
                            </h3>
                            {overviewTopParticipants.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("marathon:no_data")}
                                </p>
                            ) : (
                                <ul className="space-y-3">
                                    {overviewTopParticipants.map(
                                        (participant, idx) => (
                                            <li
                                                key={`${participant.user?.id}-${idx}`}
                                                className="flex items-center justify-between"
                                            >
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {participant.user
                                                            ?.name ?? "—"}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {participant.user
                                                            ?.email ?? "—"}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                        {participant.amount}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {
                                                            participant.participations
                                                        }{" "}
                                                        {t(
                                                            "marathon:metrics.participants"
                                                        )}
                                                    </p>
                                                </div>
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "participants" && (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("marathon:participants_filters.status")}
                                </label>
                                <select
                                    value={participantFilters.status}
                                    onChange={(e) =>
                                        handleParticipantFilterChange(
                                            "status",
                                            e.target.value
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                >
                                    {participantStatusOptions.map((option) => {
                                        const count = participantsData?.status_counts?.[option.value as keyof typeof participantsData.status_counts] ?? 0;
                                        return (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {t(option.label)} ({count})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("marathon:participants_filters.payment")}
                                </label>
                                <select
                                    value={participantFilters.paid}
                                    onChange={(e) =>
                                        handleParticipantFilterChange(
                                            "paid",
                                            e.target.value
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                >
                                    <option value="all">
                                        {t(
                                            "marathon:participants_filters.payment_all"
                                        )}
                                    </option>
                                    <option value="paid">
                                        {t(
                                            "marathon:participants_filters.payment_paid"
                                        )}
                                    </option>
                                    <option value="unpaid">
                                        {t(
                                            "marathon:participants_filters.payment_unpaid"
                                        )}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("marathon:participants_filters.search")}
                                </label>
                                <input
                                    type="text"
                                    value={participantFilters.search}
                                    onChange={(e) =>
                                        handleParticipantFilterChange(
                                            "search",
                                            e.target.value
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                        {participantsFetching ? (
                            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                {t("common:loading")}
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_labels.user")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_labels.amount")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Оплачивает
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Платит за
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Предыдущий участник
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t(
                                                "marathon:table_labels.payment_date"
                                            )}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_columns.status")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {participantsData?.data.length ? (
                                        participantsData.data.map(
                                            (participant) => (
                                                <tr key={participant.id}>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.id}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.user
                                                            ?.name ?? "—"}{" "}
                                                        (ID:
                                                        {
                                                            participant.user?.id
                                                        },{" "}
                                                        {
                                                            participant.user
                                                                ?.secret_cookie
                                                        }
                                                        )
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.amount}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.payer ? (
                                                            <>
                                                                {
                                                                    participant
                                                                        .payer
                                                                        .name
                                                                }{" "}
                                                                (ID:
                                                                {
                                                                    participant
                                                                        .payer
                                                                        .id
                                                                }
                                                                ,{" "}
                                                                {
                                                                    participant
                                                                        .payer
                                                                        .secret_cookie
                                                                }
                                                                )
                                                            </>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.pays_for ? (
                                                            <>
                                                                {
                                                                    participant
                                                                        .pays_for
                                                                        .name
                                                                }{" "}
                                                                (ID:
                                                                {
                                                                    participant
                                                                        .pays_for
                                                                        .id
                                                                }
                                                                ,{" "}
                                                                {
                                                                    participant
                                                                        .pays_for
                                                                        .secret_cookie
                                                                }
                                                                )
                                                            </>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.previous_participant ? (
                                                            <>
                                                                {
                                                                    participant
                                                                        .previous_participant
                                                                        .name
                                                                }{" "}
                                                                (ID:
                                                                {
                                                                    participant
                                                                        .previous_participant
                                                                        .id
                                                                }
                                                                ,{" "}
                                                                {
                                                                    participant
                                                                        .previous_participant
                                                                        .secret_cookie
                                                                }
                                                                )
                                                                <br />
                                                                <span className="text-gray-500 dark:text-gray-400">
                                                                    Сумма:{" "}
                                                                    {
                                                                        participant
                                                                            .previous_participant
                                                                            .amount
                                                                    }
                                                                </span>
                                                            </>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {participant.fee_paid_at
                                                            ? new Date(
                                                                  participant.fee_paid_at
                                                              ).toLocaleString()
                                                            : "—"}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm">
                                                        {renderStatusBadge(
                                                            participant.status_label
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm">
                                                        <div className="flex gap-2 items-center justify-start">
                                                            <button
                                                                onClick={() => {
                                                                    if (participant.user?.id) {
                                                                        setSelectedAccountsParticipant({
                                                                            id: participant.id,
                                                                            userId: participant.user.id,
                                                                            name: participant.user.name || "—"
                                                                        });
                                                                        setAccountsModalOpen(true);
                                                                    } else {
                                                                        showError("Пользователь не привязан к этому участнику");
                                                                    }
                                                                }}
                                                                className="flex align-center justify-center p-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50"
                                                                title="Список аккаунтов пользователя и билетов"
                                                            >
                                                                <ListIcon className="w-4 h-4" />
                                                            </button>
                                                            {!participant.fee_paid_at &&
                                                                participant.status === 0 &&
                                                                !participant.payer &&
                                                                !participant.has_reward && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handlePayParticipant(
                                                                                participant.id
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isPayingParticipant
                                                                        }
                                                                    className="flex align-center justify-center p-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                                                    title="Оплатить участие в марфоне со счета пользователя"
                                                                    >
                                                                        {isPayingParticipant
                                                                            ? t(
                                                                                  "common:loading"
                                                                              )
                                                                            : <BanknoteArrowUpIcon className="w-4 h-4" />}
                                                                    </button>
                                                                )}
                                                            {!participant.previous_participant && (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedParticipant(
                                                                            participant.id
                                                                        );
                                                                        setReplaceModalOpen(
                                                                            true
                                                                        );
                                                                    }}
                                                                    className="flex align-center justify-center p-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                                                    title="Замена одного участника на другого"
                                                                >
                                                                    <ReplaceIcon className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {participant.status === 1 && participant.user?.id && participant.user.id !== 1 && (
                                                                <button
                                                                    onClick={() => handleRestoreParticipant(participant.user!.id)}
                                                                    disabled={isRestoringParticipant}
                                                                    className="flex align-center justify-center p-1 text-xs font-medium text-white bg-green-500 rounded hover:bg-green-600 disabled:opacity-50"
                                                                    title="Восстановить участника"
                                                                >
                                                                    <ArchiveRestoreIcon className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {!participant.previous_participant &&
                                                                participant.user?.id &&
                                                                participant.user.id !== 1 && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleOpenTransferToCompany(
                                                                                participant.id,
                                                                                `${participant.user?.name ?? "—"} (ID: ${participant.user?.id ?? "—"})`
                                                                            )
                                                                        }
                                                                        className="flex align-center justify-center p-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                                                                        title="Передать участника на компанию"
                                                                    >
                                                                        <ClipboardCheckIcon className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                                            >
                                                {t("marathon:no_participants")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "tickets" && (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("marathon:tickets_filters.status")}
                                </label>
                                <select
                                    value={ticketFilters.status}
                                    onChange={(e) =>
                                        handleTicketFilterChange(
                                            "status",
                                            e.target.value
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                >
                                    {ticketStatusOptions.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {t(option.label)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("marathon:tickets_filters.search")}
                                </label>
                                <input
                                    type="text"
                                    value={ticketFilters.search}
                                    onChange={(e) =>
                                        handleTicketFilterChange(
                                            "search",
                                            e.target.value
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                        {ticketsFetching ? (
                            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                {t("common:loading")}
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_labels.owner")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            За кого получен
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            ID выбранного участника
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            ID билета-источника
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_columns.status")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t(
                                                "marathon:table_labels.created_at"
                                            )}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Время выплаты наград
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Дата анулирования
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {ticketsData?.data.length ? (
                                        ticketsData.data.map((ticket) => (
                                            <tr key={ticket.id}>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.id}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.owner?.name ?? "—"}{" "}
                                                    (ID:
                                                    {ticket.owner?.id},{" "}
                                                    {
                                                        ticket.owner
                                                            ?.secret_cookie
                                                    }
                                                    )
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.for_user ? (
                                                        <>
                                                            {
                                                                ticket.for_user
                                                                    .name
                                                            }{" "}
                                                            (ID:
                                                            {ticket.for_user.id}
                                                            ,{" "}
                                                            {
                                                                ticket.for_user
                                                                    .secret_cookie
                                                            }
                                                            )
                                                        </>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.choosen_id ?? "—"}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.for_ticket_id ??
                                                        "—"}
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                    {renderStatusBadge(
                                                        ticket.status_label
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.ticket_time
                                                        ? new Date(
                                                              ticket.ticket_time
                                                          ).toLocaleString()
                                                        : "—"}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.paid_at
                                                        ? new Date(
                                                              ticket.paid_at
                                                          ).toLocaleString()
                                                        : "—"}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {ticket.deleted_at
                                                        ? new Date(
                                                              ticket.deleted_at
                                                          ).toLocaleString()
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                                            >
                                                {t("marathon:no_tickets")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "bets" && (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:bets_filters.search")}
                        </label>
                        <input
                            type="text"
                            value={betFilters.search}
                            onChange={(e) =>
                                handleBetFilterChange(e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                        {betsFetching ? (
                            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                {t("common:loading")}
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_labels.user")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_columns.status")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t(
                                                "marathon:table_labels.created_at"
                                            )}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {betsData?.data.length ? (
                                        betsData.data.map((bet) => (
                                            <tr key={bet.id}>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {bet.user?.name ?? "—"}
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                    {bet.chosen_participant?.user ? (
                                                        <span>
                                                            {bet.chosen_participant.user.name}
                                                            {bet.chosen_participant.user.id &&
                                                            bet.chosen_participant.user.secret_cookie
                                                                ? ` (${bet.chosen_participant.user.id}, ${bet.chosen_participant.user.secret_cookie})`
                                                                : bet.chosen_participant.user.id
                                                                  ? ` (${bet.chosen_participant.user.id})`
                                                                  : ""}
                                                        </span>
                                                    ) : bet.chosen_participant?.name ? (
                                                        bet.chosen_participant.name
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {bet.created_at
                                                        ? new Date(
                                                              bet.created_at
                                                          ).toLocaleString("ru-RU", {
                                                              year: "numeric",
                                                              month: "2-digit",
                                                              day: "2-digit",
                                                              hour: "2-digit",
                                                              minute: "2-digit",
                                                              second: "2-digit",
                                                          })
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={3}
                                                className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                                            >
                                                {t("marathon:no_bets")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "winners" && (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("marathon:winners_description", {
                                defaultValue:
                                    "Билеты победителей (status=1). Порог выплаты: paid_time старше 20 дней.",
                            })}
                            {winnersData?.meta?.pay_time_threshold_display && (
                                <span className="ml-1">
                                    ({t("marathon:winners_pay_threshold", {
                                        defaultValue: "порог",
                                    })}{" "}
                                    {winnersData.meta.pay_time_threshold_display})
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                        {winnersFetching ? (
                            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                {t("common:loading")}
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            ID билета
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("marathon:table_labels.user")}
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Время выплаты
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {winnersData?.data?.length ? (
                                        winnersData.data.map((winner) => (
                                            <tr key={winner.ticket_id}>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {winner.ticket_id}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {winner.user?.name ?? "—"} (ID:{" "}
                                                    {winner.user_id}
                                                    {winner.user?.secret_cookie
                                                        ? `, ${winner.user.secret_cookie}`
                                                        : ""}
                                                    )
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                    {winner.paid_time_display ??
                                                        "—"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={3}
                                                className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                                            >
                                                {t("marathon:no_winners", {
                                                    defaultValue:
                                                        "Нет победителей",
                                                })}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Модалка аккаунтов и билетов пользователя */}
            <MarathonUserAccountsModal
                isOpen={accountsModalOpen}
                onClose={() => {
                    setAccountsModalOpen(false);
                    setSelectedAccountsParticipant(null);
                }}
                marathonId={marathonId}
                participantId={selectedAccountsParticipant?.id || 0}
                userId={selectedAccountsParticipant?.userId || 0}
                userName={selectedAccountsParticipant?.name || ""}
            />

            {/* Модалка замены участника */}
            <ReplaceParticipantModal
                isOpen={replaceModalOpen}
                onClose={() => {
                    setReplaceModalOpen(false);
                    setSelectedParticipant(null);
                }}
                participant={
                    selectedParticipant
                        ? participantsData?.data.find(
                              (p) => p.id === selectedParticipant
                          ) ?? null
                        : null
                }
                marathonId={marathonId}
                onSuccess={() => {
                    // Данные обновятся автоматически через invalidatesTags
                }}
            />

            {transferToCompanyOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Переоформить на компанию
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setTransferToCompanyOpen(false);
                                    setTransferParticipantId(null);
                                    setTransferParticipantLabel("");
                                }}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                            <p>
                                Вы собираетесь переоформить аккаунты участника{" "}
                                <span className="font-semibold">
                                    {transferParticipantLabel}
                                </span>{" "}
                                на компанию (ID: 1).
                            </p>
                            <p>
                                Действие необратимо: участия будут перенесены на компанию, а билеты других пользователей,
                                полученные за этого участника, будут удалены.
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-5">
                            <button
                                type="button"
                                onClick={() => {
                                    setTransferToCompanyOpen(false);
                                    setTransferParticipantId(null);
                                    setTransferParticipantLabel("");
                                }}
                                className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                                disabled={isTransferringToCompany}
                            >
                                Отменить
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmTransferToCompany}
                                className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                                disabled={isTransferringToCompany}
                            >
                                {isTransferringToCompany ? "Выполняем..." : "Продолжить"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarathonDetailsPage;

